import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface AuthState {
  token: string | null;
  email: string | null;
  isAuthenticated: boolean;
  isInitializing: boolean;
  hasCompletedOnboarding: boolean;
  needsOnboarding: boolean;
  lastLogin: string | null;
  tier?: string;
  user?: {
    id?: string;
    email?: string;
    tier?: string;
    isAdmin?: boolean;
    profile?: any;
  };
}

const initialState: AuthState = {
  token: localStorage.getItem("token"),
  email: localStorage.getItem("email"),
  isAuthenticated: false,
  isInitializing: true,
  hasCompletedOnboarding: localStorage.getItem("hasCompletedOnboarding") === "true",
  needsOnboarding: false,
  lastLogin: localStorage.getItem("lastLogin"),
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    loginSuccess(
      state,
      action: PayloadAction<{ token: string; email: string; hasCompletedOnboarding?: boolean; user?: any }>,
    ) {
      state.token = action.payload.token;
      state.email = action.payload.email;
      state.isAuthenticated = true;
      state.isInitializing = false;
      state.hasCompletedOnboarding = action.payload.hasCompletedOnboarding || false;
      state.needsOnboarding = !action.payload.hasCompletedOnboarding;
      state.lastLogin = new Date().toISOString();
      state.user = action.payload.user;

      // Update localStorage
      localStorage.setItem("token", action.payload.token);
      localStorage.setItem("email", action.payload.email);
      localStorage.setItem("lastLogin", state.lastLogin);
      localStorage.setItem("hasCompletedOnboarding", String(action.payload.hasCompletedOnboarding || false));
    },

    logout(state) {
      state.token = null;
      state.email = null;
      state.isAuthenticated = false;
      state.isInitializing = false;
      state.hasCompletedOnboarding = false;
      state.needsOnboarding = false;
      state.user = undefined;

      // Clear localStorage
      localStorage.removeItem("token");
      localStorage.removeItem("email");
      localStorage.removeItem("hasCompletedOnboarding");
      // Don't remove lastLogin - keep it for analytics
    },

    initializationComplete(state) {
      state.isInitializing = false;
    },

    completeOnboarding(state) {
      state.hasCompletedOnboarding = true;
      state.needsOnboarding = false;
      localStorage.setItem("hasCompletedOnboarding", "true");
    },

    requireOnboarding(state) {
      state.needsOnboarding = true;
      state.hasCompletedOnboarding = false;
      localStorage.setItem("hasCompletedOnboarding", "false");
    },

    refreshToken(state, action: PayloadAction<string>) {
      state.token = action.payload;
      localStorage.setItem("token", action.payload);
    },

    // Handle session expiration
    sessionExpired(state) {
      state.token = null;
      state.isAuthenticated = false;
      localStorage.removeItem("token");
      // Keep email and lastLogin for quicker re-login
    },

    setUser(state, action: PayloadAction<{ email: string; tier?: string }>) {
      state.email = action.payload.email;
      if (action.payload.tier) {
        state.tier = action.payload.tier;
      }
      localStorage.setItem("email", action.payload.email);
      if (action.payload.tier) {
        localStorage.setItem("tier", action.payload.tier);
      }
    },
  },
});

export const { loginSuccess, logout, refreshToken, sessionExpired, setUser, initializationComplete, completeOnboarding, requireOnboarding } =
  authSlice.actions;

// Selectors
export const selectIsAuthenticated = (state: { auth: AuthState }) =>
  state.auth.isAuthenticated;
export const selectToken = (state: { auth: AuthState }) => state.auth.token;
export const selectEmail = (state: { auth: AuthState }) => state.auth.email;

export default authSlice.reducer;
